import { useState } from 'preact/hooks'
import { useFormInput } from './useFormInput'
import { formatMobile } from '@/components/helpers/utils'
import { accountTranslations } from '../helpers/translations'

/**
 * Shared create-account logic for the two places a shopper fills in their details: the loyalty
 * drawer's "Tell us more about you" step and the Fit Club landing page's embedded form. The
 * designs order the fields differently and only the drawer collects comms preferences, so the
 * markup lives in each component and only the payload, validation and error mapping live here.
 *
 * @param {object} options
 * @param {string} [options.email] Pre-fills the email when the shopper already typed it.
 * @param {string} [options.mobile] Pre-fills the mobile when they joined via the mobile channel.
 * @param {boolean} [options.collectComms] Whether the form asks for marketing consent. One consent
 *   covers both channels: the copy shoppers agree to names the messages, not the medium.
 * @param {(customer: {id: string, name: string}) => void} options.onSuccess
 */
export const useSignUpDetails = ({ email = '', mobile = '', collectComms = false, onSuccess }) => {
  const [error, setError] = useState(null)
  const [submitting, setSubmitting] = useState(false)

  const { formInputs, handleOnChange } = useFormInput({
    email,
    firstName: '',
    lastName: '',
    mobile,
    childBirthDay: '',
    childBirthMonth: '',
    childBirthYear: '',
    marketingOptIn: false,
    termsAccepted: false
  })

  /**
   * Omneo stores each channel as its own flag rather than one preference, so an opt-in fans out
   * across every message type for that channel. Without the switches every flag stays false and
   * the matching optout is set, which is the same shape as an explicit decline.
   */
  const buildComms = () => {
    const optedIn = collectComms ? formInputs.marketingOptIn.value : false
    const emailOptIn = optedIn
    const smsOptIn = optedIn

    return {
      email_promo: emailOptIn,
      email_benefits: emailOptIn,
      email_reminders: emailOptIn,
      email_service: emailOptIn,
      email_optout: !emailOptIn,
      sms_promo: smsOptIn,
      sms_benefits: smsOptIn,
      sms_reminders: smsOptIn,
      sms_service: smsOptIn,
      sms_optout: !smsOptIn
    }
  }

  const handleSubmit = async (event) => {
    event.preventDefault()
    setError(null)
    setSubmitting(true)

    if (!window.omneo?.portal) {
      setSubmitting(false)
      return
    }

    /* Mobile is optional on both forms, so it is omitted rather than sent empty. */
    const mobile = formatMobile(formInputs.mobile.value)

    const accountData = {
      email: formInputs.email.value,
      first_name: formInputs.firstName.value,
      last_name: formInputs.lastName.value,
      ...(mobile && { mobile_phone: mobile }),
      attributes: {
        comms: buildComms()
      }
    }

    const { data, errors } = await window.omneo.portal.createAccount(accountData)

    if (errors) {
      const keys = Object.keys(errors)
      if (keys.includes('mobile_phone')) {
        setError({ type: 'mobile_phone', message: errors.mobile_phone[0] ?? 'Error' })
      } else if (keys.includes('email')) {
        setError({ type: 'email', message: errors.email[0] ?? 'Error' })
      } else {
        setError({
          type: 'error',
          message: errors.error?.[0] ?? accountTranslations.account_portal.sign_up_generic_error
        })
      }
      setSubmitting(false)
    }

    if (!data) return

    window.BCDataLayerManager?.manager?.push('sign_up', {})

    // Read once by the dashboard to decide whether to run onboarding.
    localStorage.setItem('isFirstLogin', 'true')

    onSuccess({
      id: data.identity,
      name: [data.first_name || '', data.last_name || ''].join(' ')
    })
  }

  return { formInputs, handleOnChange, handleSubmit, submitting, error }
}
